import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';

// Event names for GIS functionality
const GIS_EVENT_NAMES = {
  // Layer events
  TOGGLE_SUPPLEMENTAL_LAYER: 'toggle_supplemental_layer',
  TOGGLE_BASEMAP_LAYER: 'toggle_basemap_layer',
  LAYER_VISIBLE_DURATION: 'map_layer_visible_duration',
  LAYER_INIT_STATE: 'map_layer_init_state',

  // Markup events
  TOGGLE_MARKUP_MODE: 'toggle_markup_mode',
  USE_COMPASS_NAVIGATION: 'use_compass_navigation',
};

// =============================================================================
// LAYER TRACKING
// =============================================================================

/**
 * Tracks when a user toggles a supplemental layer visibility.
 * @param {Object} params - The parameters for tracking the supplemental layer toggle
 * @param {string} params.layerName - The name of the layer being toggled
 * @param {boolean} params.isVisible - Whether the layer is being shown (true) or hidden (false)
 * @param {string} params.layerCategory - The category of the supplemental layer (e.g., 'weather', 'traffic', 'infrastructure')
 */
export const trackToggleSupplementalLayer = ({
  layerName,
  isVisible,
  layerCategory,
}: {
  layerName: string;
  isVisible: boolean;
  layerCategory: string;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_SUPPLEMENTAL_LAYER,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      layer_category: layerCategory,
      visibility_state: isVisible ? 'show' : 'hide',
    },
    entities: {
      layer_name: layerName,
    },
  });
};

/**
 * Tracks when a user toggles a basemap layer visibility.
 * @param {Object} params - The parameters for tracking the basemap layer toggle
 * @param {string} params.layerName - The name of the basemap being toggled
 * @param {boolean} params.isVisible - Whether the basemap is being shown (true) or hidden (false)
 * @param {string} params.basemapProvider - The provider of the basemap (e.g., 'google', 'esri', 'mapbox')
 */
export const trackToggleBasemapLayer = ({
  layerName,
  isVisible,
  basemapProvider,
}: {
  layerName: string;
  isVisible: boolean;
  basemapProvider: string;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_BASEMAP_LAYER,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      basemap_provider: basemapProvider,
      visibility_state: isVisible ? 'show' : 'hide',
    },
    entities: {
      layer_name: layerName,
    },
  });
};

/** `map_unmount` and `session_end` durations are right-censored. */
export type LayerVisibleEndReason = 'layer_off' | 'map_unmount' | 'session_end';

/** Disambiguates layer names shared across layer systems. */
export type LayerVisibleType = 'spatial' | 'gis' | 'additional';

export const trackLayerVisibleDuration = ({
  layerName,
  durationMs,
  wasDefault,
  layerType,
  endReason,
}: {
  layerName: string;
  durationMs: number;
  wasDefault: boolean;
  layerType: LayerVisibleType;
  endReason: LayerVisibleEndReason;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.LAYER_VISIBLE_DURATION,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      duration_ms: durationMs,
      was_default: wasDefault,
      layer_type: layerType,
      end_reason: endReason,
    },
    entities: {
      layer_name: layerName,
    },
  });
};

/** `configured` combines catalog defaults and agency overrides. */
export type LayerInitSource =
  | 'configured'
  /** Reserved in the Amplitude schema; currently not emitted. */
  | 'system'
  | 'persisted_user';

/** Emitted once per layer per page session; presence indicates exposure. */
export const trackLayerInitState = ({
  layerName,
  enabled,
  source,
  layerType,
  mapProvider,
}: {
  layerName: string;
  enabled: boolean;
  source: LayerInitSource;
  layerType: LayerVisibleType;
  mapProvider: string;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.LAYER_INIT_STATE,
    type: GPX_EVENT_TYPES.INFO,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      enabled,
      source,
      layer_type: layerType,
      // Captures users who keep the default provider without opening its menu.
      map_provider: mapProvider,
    },
    entities: {
      layer_name: layerName,
    },
  });
};

// =============================================================================
// MARKUP TRACKING
// =============================================================================

/**
 * Tracks when a user toggles markup mode on or off.
 * @param {Object} params - The parameters for tracking markup mode toggle
 * @param {boolean} params.isEnabled - Whether markup mode is being enabled (true) or disabled (false)
 */
export const trackToggleMarkupMode = ({
  isEnabled,
}: {
  isEnabled: boolean;
}) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.TOGGLE_MARKUP_MODE,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      markup_state: isEnabled ? 'enable' : 'disable',
    },
  });
};

/**
 * @param {Object} params - The parameters for tracking compass usage
 * @param {string} params.action - The compass action performed (rotate, reset, click_direction)
 */
export const trackUseCompassNavigation = ({ action }: { action: string }) => {
  gspx.trackEvent({
    name: GIS_EVENT_NAMES.USE_COMPASS_NAVIGATION,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.GIS,
    capability: GPX_CAPABILITIES.GIS,
    route: GPX_ROUTES.MAP,
    additionalKeys: {
      compass_action: action,
    },
  });
};
